import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Account } from "../api.js";
import { useNumberFormat } from "../contexts/numberFormat/index.js";
import { Modal } from "../components/Modal.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Label } from "../components/ui/label.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";
import { majorToMinor, minorToMajorInput } from "../money.js";
import { ACCOUNT_KINDS, isManualAccountKind } from "@finance-os/shared-types/constants";

// Single source of truth: derived from the account_kind DB enum (TK-135).
const KINDS = ACCOUNT_KINDS;

export function Accounts(): JSX.Element {
  const qc = useQueryClient();
  const { formatMoney } = useNumberFormat();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const q = useQuery({ queryKey: ["accounts"], queryFn: () => api.list<Account>("/accounts") });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });

  return (
    <>
      <PageHeader title="Accounts">
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          + Add account
        </Button>
      </PageHeader>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Institution</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Last sync</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.items.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell>{a.kind}</TableCell>
                <TableCell>{a.institution ?? "—"}</TableCell>
                <TableCell>{a.currency}</TableCell>
                <TableCell className="text-right">
                  {formatMoney(a.current_balance_minor, a.currency)}
                </TableCell>
                <TableCell>
                  {a.last_synced_at ? new Date(a.last_synced_at).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(a);
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete ${a.name}?`)) remove.mutate(a.id);
                      }}
                    >
                      Del
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {q.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                  No accounts. Click "+ Add account".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
        }}
        title={editing ? `Edit ${editing.name}` : "New account"}
      >
        <AccountForm
          initial={editing}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ["accounts"] });
          }}
        />
      </Modal>
    </>
  );
}

function AccountForm({ initial, onDone }: { initial: Account | null; onDone: () => void }): JSX.Element {
  const [kind, setKind] = useState(initial?.kind ?? "bank");
  const [name, setName] = useState(initial?.name ?? "");
  const [institution, setInstitution] = useState(initial?.institution ?? "");
  const [currency, setCurrency] = useState(initial?.currency ?? "EUR");
  const [balanceMajor, setBalanceMajor] = useState(
    initial ? minorToMajorInput(initial.current_balance_minor) : "0",
  );
  const [isLiquid, setIsLiquid] = useState(initial?.is_liquid ?? true);
  const [isRetirement, setIsRetirement] = useState(initial?.is_retirement ?? false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        kind,
        name,
        institution: institution || null,
        currency: currency.toUpperCase(),
        is_liquid: isLiquid,
        is_retirement: isRetirement,
        current_balance_minor: majorToMinor(balanceMajor),
      };
      return initial ? api.update(`/accounts/${initial.id}`, body) : api.create("/accounts", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Kind">
        <Select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
          }}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Name *">
        <TextInput
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="Revolut EUR"
        />
      </Field>
      <Field label="Institution">
        <TextInput
          value={institution}
          onChange={(e) => {
            setInstitution(e.target.value);
          }}
          placeholder="Revolut Bank UAB"
        />
      </Field>
      <Field label="Currency (ISO 4217) *">
        <TextInput
          required
          maxLength={3}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value.toUpperCase());
          }}
          placeholder="EUR"
        />
      </Field>
      <Field
        label={`${isManualAccountKind(kind) ? "Opening balance" : "Current balance"} (major units, e.g. 1234.56) *`}
      >
        <TextInput
          required
          type="number"
          step="0.01"
          value={balanceMajor}
          onChange={(e) => {
            setBalanceMajor(e.target.value);
          }}
        />
      </Field>
      <Label className="my-2 font-normal">
        <input
          type="checkbox"
          className="accent-brand size-4"
          checked={isLiquid}
          onChange={(e) => {
            setIsLiquid(e.target.checked);
          }}
        />
        <span>Counts toward emergency fund (liquid)</span>
      </Label>
      <Label className="my-2 font-normal">
        <input
          type="checkbox"
          className="accent-brand size-4"
          checked={isRetirement}
          onChange={(e) => {
            setIsRetirement(e.target.checked);
          }}
        />
        <span>Counts toward retirement projections</span>
      </Label>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving..." : initial ? "Save changes" : "Create account"}
      </Button>
    </form>
  );
}
